import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Accessibility, 
  Heart, 
  Shield, 
  DoorOpen, 
  Armchair, 
  Users, 
  Volume2, 
  Dog, 
  Sparkles,
  ClipboardCheck,
  MapPin,
  HelpCircle,
  Mail,
  AlertCircle,
  Phone,
  Calendar,
  Award,
  ChevronRight
} from 'lucide-react';
import Navigation from '../components/Navigation';

const ADAAssistancePage = () => {
  const services = [
    {
      icon: DoorOpen,
      title: 'Accessibility Entrance',
      description: 'Designated entry points for guests with mobility needs'
    },
    {
      icon: Armchair,
      title: 'Wheelchair Assistance',
      description: 'Complimentary use or rental desk available on-site'
    },
    {
      icon: Volume2,
      title: 'Sign Language Interpretation',
      description: 'Available during main stage sessions and keynotes'
    },
    {
      icon: Users,
      title: 'Reserved Seating',
      description: 'Special seating areas for guests with accessibility passes'
    },
    {
      icon: Dog,
      title: 'Service Animals',
      description: 'Permitted in all public areas with care stations available'
    },
    {
      icon: Sparkles,
      title: 'Quiet / Reflection Spaces',
      description: 'Calm environments for rest, prayer, or sensory breaks'
    }
  ];

  const registrationSteps = [
    {
      number: '1',
      title: 'Request Your ADA Badge',
      description: 'Complete our online accessibility form before the event',
      icon: ClipboardCheck
    },
    {
      number: '2',
      title: 'Visit the Accessibility Desk',
      description: 'Located at the main entrance for check-in and assistance',
      icon: MapPin
    },
    {
      number: '3',
      title: 'Enjoy Full Access',
      description: 'Our volunteer team will ensure you experience KNOWN CON fully',
      icon: Heart
    }
  ];

  const resources = [
    {
      icon: HelpCircle,
      title: 'ADA FAQs',
      description: 'Quick answers for common accessibility questions',
      link: '#faq'
    },
    {
      icon: Mail,
      title: 'Contact Accessibility Team',
      description: 'Email us directly for personalized assistance',
      link: '#contact'
    },
    {
      icon: AlertCircle,
      title: 'Emergency & Medical Support',
      description: 'On-site medical assistance and emergency contacts',
      link: '#emergency'
    }
  ];

  const testimonials = [
    {
      quote: "At KNOWN CON, I never felt limited — I felt seen, valued, and fully included in every moment.",
      author: "Sarah M.",
      role: "2024 Attendee"
    },
    {
      quote: "The accessibility team went above and beyond. They didn't just accommodate me, they welcomed me with open arms.",
      author: "Marcus J.",
      role: "First-time Attendee"
    },
    {
      quote: "From parking to seating to quiet spaces for my son, every detail was thoughtfully considered. We felt at home.",
      author: "Lisa P.",
      role: "Parent Attendee"
    }
  ];

  const quickLinks = [
    { label: 'ADA Form', href: '#form', icon: ClipboardCheck },
    { label: 'Hotel & Travel', href: '/hotel-travel', icon: MapPin },
    { label: 'Attendee Guide', href: '/attendee-guide', icon: Calendar },
    { label: 'Badge Activation', href: '#badge', icon: Award }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#0B1B3B]/80 to-[#0B1B3B]/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-gray-300">Conference Info</span>
            <span>/</span>
            <span className="text-white font-medium">ADA Assistance</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            ADA <span className="text-[#CFA64B]">Assistance</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Accessibility for every guest, every story, every purpose.
          </p>
        </div>
      </div>

      {/* Quick Link Bar */}
      <div className="bg-[#0B1B3B] py-4">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/attendee-guide" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Attendee Guide
            </a>
            <span className="text-gray-500">|</span>
            <a href="/hotel-travel" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Hotel & Travel
            </a>
            <span className="text-gray-500">|</span>
            <a href="#faq" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              FAQs
            </a>
            <span className="text-gray-500">|</span>
            <a href="#contact" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Section 1: Our Accessibility Promise */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Text */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-6">Our Promise to Every Attendee</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At KNOWN CON, we believe that every person — regardless of ability — deserves to experience the 
                full power of faith, community, and purpose. We are committed to creating an environment where 
                everyone feels welcomed, supported, and empowered to participate fully.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Your presence matters. Your story matters. And we're here to ensure that nothing stands in the 
                way of your transformative experience.
              </p>
            </div>

            {/* Right: Image */}
            <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop" 
                alt="Inclusive community" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* 3-Point Statement */}
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                  <Accessibility className="w-8 h-8 text-[#CFA64B]" />
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Equal Access to All Venues</h3>
                <p className="text-gray-600">Every space, session, and experience is designed to be accessible</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-[#CFA64B]" />
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Respect and Dignity for Every Guest</h3>
                <p className="text-gray-600">We honor your needs with compassion and understanding</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-[#CFA64B]" />
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Assistance with Joy and Compassion</h3>
                <p className="text-gray-600">Our team is trained, ready, and honored to serve you</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Section 2: On-Site Services */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Accessibility Services Available</h2>
            <p className="text-xl text-gray-600">Comprehensive support to ensure your comfort and full participation</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={idx} 
                  className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                      <Icon className="w-10 h-10 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 3: ADA Registration & Support Desk */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Register for Assistance</h2>
            <p className="text-xl text-gray-600">Three simple steps to ensure we're ready to support you</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {registrationSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <Card key={idx} className="border-none shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#CFA64B]/5 rounded-bl-full" />
                  <CardContent className="p-8 relative">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#CFA64B] text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                        {step.number}
                      </div>
                      <div className="w-12 h-12 bg-[#CFA64B]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#CFA64B]" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
              Submit ADA Request
            </Button>
          </div>
        </div>
      </div>

      {/* Section 4: Transport & Venue Accessibility */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Getting Here with Ease</h2>
            <p className="text-xl text-gray-600">Accessible routes from hotels, parking, and drop-off zones</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Description */}
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Los Angeles Convention Center is fully ADA compliant with multiple accessible entrances. 
                We've partnered with local hotels to provide complimentary shuttle services with wheelchair 
                accessibility.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#CFA64B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">ADA-compliant parking spaces at South Hall entrance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#CFA64B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Accessible drop-off zones with volunteer assistance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#CFA64B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Wheelchair-accessible shuttle from partner hotels</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#CFA64B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Elevator access to all floors and session rooms</span>
                </li>
              </ul>
              <Button variant="outline" className="mt-8 border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-white">
                <MapPin className="w-4 h-4 mr-2" />
                View Venue Map
              </Button>
            </div>

            {/* Right: Map Placeholder */}
            <div className="relative w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden shadow-xl">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-16 h-16 text-[#CFA64B] mx-auto mb-4" />
                  <p className="text-2xl font-bold text-[#0B1B3B] mb-2">Accessible Entrance Map</p>
                  <p className="text-gray-600">Los Angeles Convention Center</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Additional Resources */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">More Ways We Can Help</h2>
            <p className="text-xl text-gray-600">Additional resources and support options</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, idx) => {
              const Icon = resource.icon;
              return (
                <a
                  key={idx}
                  href={resource.link}
                  className="block group"
                >
                  <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 h-full">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                        <Icon className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B1B3B] mb-3 group-hover:text-[#CFA64B] transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{resource.description}</p>
                      <div className="flex items-center justify-center text-[#CFA64B] group-hover:gap-2 transition-all">
                        <span className="text-sm font-semibold">Learn More</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 6: Community Voices */}
      <div className="py-20 bg-gradient-to-b from-[#E9EDF6] to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Stories of Belonging</h2>
            <p className="text-xl text-gray-600">Hear from attendees who experienced KNOWN CON fully</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="text-[#CFA64B] text-6xl mb-4 leading-none">"</div>
                  <p className="text-lg text-gray-700 italic leading-relaxed mb-6">
                    {testimonial.quote}
                  </p>
                  <div className="border-t-2 border-[#CFA64B] pt-4">
                    <p className="font-bold text-[#0B1B3B]">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Section 7: Quick Links Strip */}
      <div className="py-12 bg-gradient-to-r from-[#CFA64B] to-[#d97706]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6">
            {quickLinks.map((link, idx) => {
              const Icon = link.icon;
              return (
                <a
                  key={idx}
                  href={link.href}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-full text-white font-semibold transition-all"
                >
                  <Icon className="w-5 h-5" />
                  {link.label}
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 8: Final CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            We're here to help you experience KNOWN CON fully.
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Questions? Concerns? Needs we haven't addressed? Reach out — we're listening.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
              <Phone className="w-5 h-5 mr-2" />
              Contact Support
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              Buy Tickets
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved. Accessible to all, welcoming to everyone.</p>
        </div>
      </footer>
    </div>
  );
};

export default ADAAssistancePage;
